import { Link, useLocation, useParams } from 'react-router-dom';
import classNames from 'classnames';

import {
  IconKS,
  IconNbLogout,
  IconNavQueue,
  IconNavAccountant,
  IconNavAdmin,
  IconNavQueueFilled,
  IconNavAsk,
  IconNavPayroll,
  IconNavCalendar,
  IconNavTasks,
  IconNavTeam,
} from 'components/Icons';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { HoneReportTimeframes, HoneReportTypes, MENU_ICONS, MENU_ICONS_FILLED } from '../../../constants';

import { useSidenavStore } from 'hooks/useSidenavStore';

import { reportTitleToUrl, abilityCan, showToast, TOAST_DEFAULT, FIVE_SECONDS, TOAST_SUCCESS } from 'lib/utils';
import { useLocationsStore } from 'hooks/useLocationsStore';

import { UIAskRequestsTitles } from 'types/askcustomer';
import { useReportsQuery } from 'hooks/reports/useReportsQuery';
import { HoneAbilityActions, HoneAbilitySubjects, LocationConfig, PayrollStatuses } from '@hone-automation/common';
import { useAskRequestsPendingCount } from 'hooks/useAskRequestsPendingCount';

import SideNavAccrualsLink from './SideNavAccrualsLink';
import SideNavIcon from './SideNavIcon';
import { useAuthContext } from 'context/useAuthContext';

import { Can } from '@casl/react';
import { isEmpty } from 'lodash';
import { format } from 'date-fns-tz';
import { endOfMonth, startOfMonth } from 'date-fns';
import { useTemplatesRangesQuery } from '../../../hooks/reports/useTemplatesRangesQuery';
import { useOpenRollfiPayroll } from 'hooks/rollfi';
import { Whisper } from 'rsuite';
import { Tooltip } from 'rsuite';

const closedTitles = {
  'P&L': 'P&L',
  'Manager Dashboard': 'Manager',
  'Balance Sheet': 'Balance',
  'Cash Flow': 'Cash',
  'YTD Monthly Income Statement': 'YTD',
  Forecast: 'FST',
  Bookkeeper: 'Accountant',
  Dashboard: 'Home',
  'Ask Requests': 'Requests',
  'AP Aging': 'Vendors',
  'Journal Entries': 'Entries',
  'Bkpr Admin': 'Admin',
  Tasks: 'Tasks',
  Teams: 'Teams',
  Payroll: 'Payroll',
  Calendar: 'Calendar',
  Queue: 'Queue',
  Rollfi: 'KS Payroll',
};

const isBeta = (value: string) => {
  return ['none'].includes(value); //"Forecast", "Ask Requests", "AP Aging", "Journal Entries"
};

function Sidenav() {
  const { data: availableTemplates } = useTemplatesRangesQuery();
  const { currentLocationAbility, currentLocation: userLocation, userInfo } = useAuthContext();

  const { enableQueue } = useFlags();

  const simplyVersion = __APP_VERSION__.replace(/-develop./g, '-D');
  const versionType = __APP_VERSION__.includes('develop')
    ? 'Development'
    : __APP_VERSION__.includes('RC')
      ? 'Release Candidate'
      : 'Production';

  const auth = useAuthContext();

  const handleSignOut = async () => {
    await auth.signOut();
    localStorage.removeItem('recentLocations');
  };

  const menuItemsMap = new Map<string, HoneReportSummary[]>();
  menuItemsMap.set('P&L', []);
  menuItemsMap.set('Balance Sheet', []);
  menuItemsMap.set('Cash Flow', []);
  menuItemsMap.set('AP Aging', []);

  useReportsQuery(reports => {
    if (reports) {
      reports.forEach(report => {
        switch (report.type) {
          case HoneReportTypes.PLComparison:
            if (report.timeframe === HoneReportTimeframes.Weekly) {
              menuItemsMap.set('P&L', [...menuItemsMap.get('P&L')!, report]);
            } else if (report.timeframe === HoneReportTimeframes.Monthly) {
              menuItemsMap.set('P&L', [...menuItemsMap.get('P&L')!, report]);
            }
            break;
          case HoneReportTypes.BalanceSheet:
            menuItemsMap.set('Balance Sheet', [...menuItemsMap.get('Balance Sheet')!, report]);
            break;
          case HoneReportTypes.CashFlow:
            menuItemsMap.set('Cash Flow', [...menuItemsMap.get('Cash Flow')!, report]);
            break;
          case HoneReportTypes.IncomeStatement:
            menuItemsMap.set('P&L', [...menuItemsMap.get('P&L')!, report]);
            break;
          case HoneReportTypes.ApAging:
            menuItemsMap.set('AP Aging', [...menuItemsMap.get('AP Aging')!, report]);
            break;
        }
      });
    }
    return reports;
  });

  const menuItems = new Map([...menuItemsMap].filter(([k, v]) => v.length > 0));

  const { locationId: currentLocationId } = useParams();

  const { pathname } = useLocation();
  const currentLocation = useLocationsStore(state => state.currentLocation);

  const { isOwner, currentLocationAbilities } = useAuthContext();
  const { toggleSidenav } = useSidenavStore();

  const shouldCollapse = true; //isMobile ? !isSidenavCollapsed : isSidenavCollapsed;

  const askRequestsCountForCurrentRole = useAskRequestsPendingCount();

  const canReadBookkeeperControls = abilityCan(
    currentLocationAbilities,
    HoneAbilityActions.read,
    HoneAbilitySubjects.Bookkeeper_Controls
  );

  const menuItemsEnabled = Array.from(menuItems.keys());

  // Check if Rollfi is configured for this location
  const rollfiEnabled = userInfo?.userLocationConfig
    ? JSON.parse(userInfo.userLocationConfig as unknown as string)?.payrollStatus === PayrollStatuses.Active
    : false;
  const { mutate: openRollfiPayroll, isPending: isOpeningRollfiPayroll } = useOpenRollfiPayroll();

  const handleOpenRollfiPayroll = () => {
    showToast('Opening Rollfi platform in a new tab...', TOAST_SUCCESS, FIVE_SECONDS);
    openRollfiPayroll();
  };

  const payLinks: LocationConfig = {
    links: {
      paymentLink: currentLocation?.config?.links?.paymentLink ?? '',
      payroll: currentLocation?.config?.links?.payroll ?? '',
      v2: currentLocation?.config?.links?.v2 ?? '',
      tasks: currentLocation?.config?.links?.tasks ?? '',
      teams: currentLocation?.config?.links?.teams ?? '',
    },
  };

  const showPayroll = payLinks.links && payLinks.links.payroll && !rollfiEnabled;

  return (
    <div id="SidenavMenu" className={classNames('NavigationMenu', { 'NavigationMenu-closed': shouldCollapse })}>
      <div className="NavigationMenu-logo-user">
        <Link to={`/app/location/${currentLocationId}`}>
          <IconKS />
        </Link>
      </div>

      <ul className="NavigationMenu-group">
        <li className="NavigationMenu-groupItem">
          <Link title={'Home'} to={`/app/location/${currentLocationId}/new-dashboard`}>
            <SideNavIcon
              Icon={IconNavQueue}
              SelectedIcon={IconNavQueueFilled}
              selected={pathname.includes('/new-dashboard')}
              title={'Queue'}
              tooltip={'Queue'}
            />
          </Link>

          {menuItemsEnabled.map((key, idx) => {
            const menuItem = menuItems.get(key);

            if (!menuItem) return null;

            let currentReport: TemplateRange | HoneReportSummary | undefined =
              key === 'P&L' ? availableTemplates?.[0] : undefined;

            if (!currentReport) {
              currentReport = menuItem.find(it => it.timeframe === HoneReportTimeframes.Monthly);
            }
            if (!currentReport) {
              currentReport = menuItem.find(it => it.type === HoneReportTypes.IncomeStatement);
            }

            if (key === 'AP Aging') {
              currentReport = menuItem.find(it => {
                return it.type === HoneReportTypes.ApAging;
              });
            }

            if (!currentReport) {
              return;
            }
            const Icon = MENU_ICONS[key as keyof typeof MENU_ICONS];
            const SelectedIcon = MENU_ICONS_FILLED[key as keyof typeof MENU_ICONS_FILLED];
            let reportUrlTitle = reportTitleToUrl(key, true);

            if (reportUrlTitle === 'pl') {
              reportUrlTitle = currentReport.timeframe.toLowerCase().includes('ytd')
                ? 'income-statement'
                : currentReport.timeframe.toLowerCase();
            }

            const templateId = ('templateId' in currentReport ? currentReport.templateId : currentReport.id) as string;

            const selected =
              pathname.includes(currentReport.id) ||
              pathname.includes(reportUrlTitle) ||
              (['income-statement', 'monthly', 'weekly'].some(v => pathname.includes(v)) &&
                reportTitleToUrl(key, true) === 'pl');

            let to =
              reportTitleToUrl(key, true) === 'pl'
                ? `/app/location/${currentLocationId}/report/${reportUrlTitle}?templateId=${templateId}`
                : `/app/location/${currentLocationId}/report/${reportUrlTitle}`;

            const urlParams = new URLSearchParams({});
            if (reportTitleToUrl(key, true) === 'pl') {
              const budgetRange = 'budgetRanges' in currentReport ? currentReport.budgetRanges : undefined;
              const templateRange = 'range' in currentReport ? currentReport.range : undefined;

              let userStart: string | undefined;
              let userEnd: string | undefined;

              if (currentReport.timeframe === HoneReportTimeframes.Weekly) {
                userStart = templateRange?.start;
                userEnd = templateRange?.end;
              } else {
                userStart = format(startOfMonth(new Date()), 'yyy-MM-dd');
                userEnd = format(endOfMonth(new Date()), 'yyy-MM-dd');
              }

              const hasBudgets =
                userStart && userEnd && budgetRange
                  ? budgetRange.start <= userEnd && budgetRange.end >= userStart
                  : false;

              urlParams.set('templateId', templateId);
              urlParams.set('chartCollapsed', 'true');
              urlParams.set('type', 'P&L Comparison');
              urlParams.set('difference', 'false');
              urlParams.set('reverse', 'false');
              urlParams.set('budget', String(hasBudgets));
              urlParams.set('customDateRange', 'false');
              urlParams.set('breakdownPeriods', 'false');

              if (currentReport.timeframe === HoneReportTimeframes.Weekly) {
                urlParams.set('timeframe', 'Week');
                urlParams.set('comparisonType', 'Prior Period');
                urlParams.set('comparisonPeriods', '4');
                urlParams.set('compareEnabled', 'true');
              } else {
                urlParams.set('timeframe', 'Month');
                urlParams.set('dateRange', `${userStart},${userEnd}`);
              }

              to = `/app/location/${currentLocationId}/report/${reportUrlTitle}?${urlParams.toString()}`;
            }

            return (
              <Link key={`link-${idx}`} to={to}>
                <SideNavIcon
                  Icon={Icon}
                  SelectedIcon={SelectedIcon}
                  selected={selected}
                  title={closedTitles[key as keyof typeof closedTitles]}
                  beta={isBeta(key)}
                  tooltip={key === 'AP Aging' ? 'Vendors' : key}
                />
              </Link>
            );
          })}
          {canReadBookkeeperControls && <SideNavAccrualsLink {...{ pathname, toggleSidenav }} isMobile={false} />}
        </li>
        <li className="NavigationMenu-groupItem">
          {/* TODO: I dont think we want to show this on prod */}
          {/*{enableQueue && (*/}
          {/*  <Link*/}
          {/*    title={UIAskRequestsTitles.ASK_REQUESTS}*/}
          {/*    to={`/app/location/${currentLocationId}/ask-requests?activeTab=Open`}*/}
          {/*    onClick={() => {*/}
          {/*      isMobile && toggleSidenav();*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    <SideNavIcon*/}
          {/*      Icon={IconNbAsk}*/}
          {/*      selected={pathname.includes('/ask-requests')}*/}
          {/*      title={closedTitles[UIAskRequestsTitles.ASK_REQUESTS as keyof typeof closedTitles]}*/}
          {/*      tooltip="Ask Requests"*/}
          {/*      badge={askRequestsCountForCurrentRole}*/}
          {/*      beta={isBeta('Ask Requests')}*/}
          {/*    />*/}
          {/*  </Link>*/}
          {/*)}*/}

          {enableQueue && (
            <Link title={UIAskRequestsTitles.QUEUE} to={`/app/location/${currentLocationId}/queue`}>
              <SideNavIcon
                Icon={IconNavAsk}
                selected={pathname.includes('/queue')}
                title={closedTitles[UIAskRequestsTitles.QUEUE as keyof typeof closedTitles]}
                tooltip={'Queue'}
                badge={askRequestsCountForCurrentRole}
                beta={isBeta('Ask Requests')}
              />
            </Link>
          )}
          {canReadBookkeeperControls && (
            <Link key={`link-bookkeeper`} to={`/app/location/${currentLocationId}/bookkeeper`} title={'Bookkeeper'}>
              <SideNavIcon
                Icon={IconNavAccountant}
                selected={pathname.includes('/bookkeeper')}
                title={closedTitles['Bookkeeper' as keyof typeof closedTitles]}
                tooltip="Accountant"
                beta={isBeta('Bookkeeper')}
              />
            </Link>
          )}

          {payLinks.links && payLinks.links.tasks && (
            <Link key="link-tasks" to={payLinks.links.tasks} target="_blank">
              <SideNavIcon
                Icon={IconNavTasks}
                selected={pathname.includes('/tasks')}
                title={closedTitles['Tasks' as keyof typeof closedTitles]}
                tooltip="Tasks"
                beta={isBeta('Tasks')}
              />
            </Link>
          )}

          {payLinks.links && payLinks.links.teams && (
            <Link key="link-teams" to={payLinks.links.teams} target="_blank">
              <SideNavIcon
                Icon={IconNavTeam}
                selected={pathname.includes('/teams')}
                title={closedTitles['Teams' as keyof typeof closedTitles]}
                tooltip="Teams"
                beta={isBeta('Teams')}
              />
            </Link>
          )}

          {payLinks.links && payLinks.links.v2 && (
            <Link key="link-v2" to={payLinks.links.v2} target="_blank">
              <SideNavIcon
                Icon={IconNavCalendar}
                selected={pathname.includes('/calendar')}
                title={closedTitles['Calendar' as keyof typeof closedTitles]}
                tooltip="Calendar"
                beta={isBeta('Calendar')}
              />
            </Link>
          )}
          {showPayroll && (
            <Link key="link-payroll" to={payLinks.links.payroll || '#'} target="_blank">
              <SideNavIcon
                Icon={IconNavPayroll}
                selected={pathname.includes('/payroll')}
                title={closedTitles['Payroll' as keyof typeof closedTitles]}
                tooltip="Payroll"
                beta={isBeta('Payroll')}
              />
            </Link>
          )}
          {rollfiEnabled && (
            <button
              key="link-rollfi-payroll"
              onClick={handleOpenRollfiPayroll}
              disabled={isOpeningRollfiPayroll}
              className="NavigationMenu-button"
            >
              <SideNavIcon
                Icon={IconNavPayroll}
                selected={false}
                title={closedTitles['Rollfi' as keyof typeof closedTitles]}
                tooltip="Click to open KitchenSync Payroll in a new tab"
                beta={isBeta('Rollfi')}
              />
            </button>
          )}

          {!isEmpty(currentLocationAbility) && (
            <Can I="update" a="Internal Controls" ability={currentLocationAbility}>
              <Link key="link-bkpr-admin" to={`/app/location/${currentLocationId}/admin`}>
                <SideNavIcon
                  Icon={IconNavAdmin}
                  selected={pathname.includes('/admin')}
                  title={closedTitles['Bkpr Admin' as keyof typeof closedTitles]}
                  tooltip="Accountant Admin"
                  beta={isBeta('Bkpr Admin')}
                />
              </Link>
            </Can>
          )}
        </li>
      </ul>
      <div className="NB-footer">
        <Whisper
          trigger="hover"
          placement="right"
          delayOpen={600}
          delayClose={100}
          speaker={
            <Tooltip>
              {versionType}
              <br></br>
              <b>{__APP_VERSION__}</b>
            </Tooltip>
          }
        >
          <div className="app-version">{simplyVersion}</div>
        </Whisper>
        <div className="NB-logout" onClick={handleSignOut}>
          <IconNbLogout />
        </div>
      </div>
    </div>
  );
}

export default Sidenav;
